import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ExternalLink, MessageCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatWIB } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { FollowUpForm } from "@/components/FollowUpForm";
import { FU_STATUS, FU_STATUS_LABELS, waLink } from "@/lib/prospectMeta";
import { useAuth } from "@/context/AuthContext";

const inputCls = "px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";
const statusCls = {
  terjadwal: "bg-blue-50 text-blue-700 border-blue-200",
  selesai: "bg-green-50 text-green-700 border-green-200",
  terlambat: "bg-red-50 text-red-700 border-red-200",
  dibatalkan: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function FollowUp() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [members, setMembers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/followups", { params: status ? { status } : {} }); setItems(data.items); }
    catch { toast.error("Gagal memuat follow-up."); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/prospects", { params: { page: 1, page_size: 200 } }).then(({ data }) => setProspects(data.items)).catch(() => {});
    api.get("/prospects/meta").then(({ data }) => setMembers(data.members)).catch(() => {});
  }, []);

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div><h1 className="text-2xl font-bold text-slate-900">Follow-up</h1><p className="text-sm text-slate-500">{items.length} catatan tindak lanjut</p></div>
        <div className="flex items-center gap-3">
          <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="followup-filter-status">
            <option value="">Semua Status</option>{FU_STATUS.map((s) => <option key={s} value={s}>{FU_STATUS_LABELS[s]}</option>)}
          </select>
          <button onClick={() => setShowForm(true)} data-testid="followup-add-button" className="inline-flex items-center gap-1.5 bg-[#f97316] hover:bg-[#ea580c] text-white font-medium px-4 py-2.5 rounded-lg"><Plus className="h-4 w-4" /> Tambah Follow-up</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="followup-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Prospek</th><th className="text-left px-4 py-3">Jadwal (WIB)</th><th className="text-left px-4 py-3">Kanal</th><th className="text-left px-4 py-3">Hasil</th><th className="text-left px-4 py-3">Berikutnya</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">PIC</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="followup-page-empty">Belum ada follow-up. Klik "Tambah Follow-up".</td></tr>
              ) : items.map((f) => (
                <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{f.prospect_name}</td>
                  <td className="px-4 py-3 text-slate-600">{formatWIB(f.scheduled_at)}</td>
                  <td className="px-4 py-3 text-slate-600">{f.channel}</td>
                  <td className="px-4 py-3 text-slate-600">{f.outcome || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{f.next_follow_up_at ? formatWIB(f.next_follow_up_at) : "-"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs border ${statusCls[f.status] || "bg-slate-100"}`}>{FU_STATUS_LABELS[f.status] || f.status}</span></td>
                  <td className="px-4 py-3 text-slate-600">{f.pic_name || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {f.prospect_phone && <a href={waLink(f.prospect_phone)} target="_blank" rel="noreferrer" data-testid={`followup-wa-${f.id}`} className="p-1.5 rounded hover:bg-green-100 text-green-600" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
                      <button onClick={() => navigate(`/prospek/${f.prospect_id}`)} data-testid={`followup-open-${f.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Buka prospek"><ExternalLink className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <FollowUpForm
          prospects={prospects}
          members={members}
          defaultPic={user?.id || ""}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </AppLayout>
  );
}
